import {
	ConfluencePageAri,
	JiraIssueAri,
	TownsquareGoalAri,
	TownsquareProjectAri,
	IdentityTeamAri,
} from '@atlassian/ari';
import {
	PARENT_PRODUCTS,
	useConfluencePagePreviewPanel,
} from '@atlassian/confluence-page-preview-panel';
import { useGlobalObjectPreviewPanels } from '@atlassian/preview-panel-global-objects';

import { useSessionData } from '@confluence/session-data';

import { ALL_SUPPORTED_OBJECTS } from './isPreviewPanelAvailable';

export interface OpenParams {
	url: string;
	ari: string;
}

// Helper function to check if we're in an iframe
const isInIframe = (): boolean => {
	try {
		return window !== window.top;
	} catch {
		// If we can't access window.top due to cross-origin restrictions, assume we're in an iframe
		return true;
	}
};

// Helper function to check if previewPanels query parameter is present
const hasPreviewPanelsParam = (): boolean => {
	try {
		const urlParams = new URLSearchParams(window.location.search);
		return urlParams.has('previewPanels');
	} catch {
		return false;
	}
};

export const useOpenPreviewPanel = () => {
	const { cloudId } = useSessionData();
	const { openPreviewPanel: openAtlasPreviewPanel } = useGlobalObjectPreviewPanels(
		PARENT_PRODUCTS.CONFLUENCE,
		cloudId,
	);
	const { openConfluencePagePreviewPanel } = useConfluencePagePreviewPanel(
		PARENT_PRODUCTS.CONFLUENCE,
	);

	const open = ({ url, ari }: OpenParams) => {
		if (isInIframe() && hasPreviewPanelsParam()) {
			// Post message to parent frame to open the panel
			window.parent.postMessage(
				{
					type: 'OPEN_PREVIEW_PANEL',
					payload: {
						url,
						ari,
					},
				},
				'*',
			);
			return;
		}

		if (ConfluencePageAri.check(ari)) {
			const urlObj = new URL(url);
			urlObj.searchParams.set('previewPanels', encodeURIComponent(ALL_SUPPORTED_OBJECTS.join(',')));

			openConfluencePagePreviewPanel(urlObj.toString());
			return;
		}
		if (TownsquareGoalAri.check(ari)) {
			openAtlasPreviewPanel({ url, ari, name: 'Goal' });
			return;
		}

		if (TownsquareProjectAri.check(ari)) {
			openAtlasPreviewPanel({ url, ari, name: 'Project' });
			return;
		}

		if (JiraIssueAri.check(ari)) {
			openAtlasPreviewPanel({ url, ari, name: 'Jira Work Item' });
			return;
		}

		if (IdentityTeamAri.check(ari)) {
			openAtlasPreviewPanel({ url, ari, name: 'Team' });
			return;
		}
	};

	return { open };
};
